import React, { useState } from 'react';
import { PaymentRequestsTableContext, IPaymentRequestTableContext } from './PaymentRequestsTableContext';
import { useAsyncData } from '@shared/react';
import { useErrorNotify } from '@features/useErrorNotify';
import { usePagination } from '@features/Pagination';
import { combineFilter, combineFilterMany, combineFilterWithOp, joinFiltersWithComma } from '@shared/utils/query';
import { getHistory } from '@widgets/PaymentRequestsTable/api';
import { useDrawer } from '@features/Modal';
import { PaymentRequestsViewDrawer } from '@widgets/PaymentRequestsTable/components/PaymentRequestsViewDrawer';
import { IServiceTransfersDto } from '@shared/api';
import moment from 'moment-timezone';

export interface IPaymentRequestsDesc {
    accountNumber: boolean;
    createdAt: boolean;
    amount: boolean;
    [key: string]: boolean;
}

export const PaymentRequestsTableProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [date, setDate] = useState<string | null>(null);
    const [search, setSearch] = useState<string>('');
    const [paymentRequestsType, setPaymentRequestsType] = useState<string[]>([]);
    const [paymentRequestsMethod, setPaymentRequestsMethod] = useState<string[]>([]);
    const [sortFieldName, setSortFieldName] = useState('createdAt');
    const [desc, setDesc] = useState<IPaymentRequestsDesc>({
        accountNumber: false,
        createdAt: true,
        amount: true,
    });

    const { page, pageSize, setTotalCount, paginationOptions } = usePagination();

    const {
        data: paymentRequests,
        loading: paymentRequestsLoading,
        error: paymentRequestsError,
        handleRefresh: paymentRequestsRefresh,
    } = useAsyncData(
        async () => {
            const dateFilter = {
                from: {
                    keyOverride: 'createdAt',
                    value: date?.length && moment(date).startOf('day').format(),
                    operator: '>=',
                },
                to: {
                    keyOverride: 'createdAt',
                    value: date?.length && moment(date).endOf('day').format(),
                    operator: '<=',
                },
            };
            const filter = {
                accountNumber: search,
            };
            const manyFilter = {
                Status: paymentRequestsType,
                Method: paymentRequestsMethod,
            };
            const { items, totalResults } = await getHistory({
                page: page,
                pageSize,
                filter: joinFiltersWithComma(
                    combineFilterWithOp(dateFilter),
                    combineFilter(filter),
                    combineFilterMany(manyFilter),
                ),
                orderBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}`,
            });
            setTotalCount(totalResults);
            return items || [];
        },
        [],
        [page, pageSize, date, search, paymentRequestsType, paymentRequestsMethod],
    );

    const handlePaymentRequestsSort = (fieldName: string) => {
        setSortFieldName(fieldName);
        setDesc((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
        paymentRequestsRefresh();
    };

    useErrorNotify(paymentRequestsError, 'Payment Requests');

    const { open: openPaymentRequestDrawer } = useDrawer(PaymentRequestsViewDrawer);

    const onPaymentRequestViewClick = (paymentRequest: IServiceTransfersDto) => {
        openPaymentRequestDrawer({ paymentRequest });
    };

    const value: IPaymentRequestTableContext = {
        date,
        paymentRequests,
        paymentRequestsLoading,
        paginationOptions,
        paymentRequestsType,
        desc,
        sortFieldName,
        handlePaymentRequestsSort,
        paymentRequestsMethod,
        onPaymentRequestViewClick,
        setDate,
        setSearch,
        setPaymentRequestsType,
        setPaymentRequestsMethod,
    };

    return <PaymentRequestsTableContext.Provider value={value}>{children}</PaymentRequestsTableContext.Provider>;
};
